import { useEffect, useRef, useState } from 'react'

import classNames from 'classnames'
import get from 'lodash/get'
import PropTypes from 'prop-types'

import { Icon, Input, ProLiteIndicator, Tooltip } from 'components/Core'

import styles from './AccountDropdown.module.css'

export const BRAND = 'Brand'
export const RETAILER = 'Retailer'

export const PROFILE_NAME = 'name'
export const ACCOUNT_ID = 'id'
export const IS_ACCOUNT_CONNECTED = 'isConnected'
export const IS_ACCOUNT_PRO = 'isPro'

const sanitizeProfileName = (profileName) =>
  profileName
    .normalize('NFD')
    .replace(/[\W_]/g, '')
    .replace(/[\u0300-\u036f]/g, '')

function AccountDropdown(props) {
  const {
    accountType,
    accounts,
    filterAccountsByInput,
    handleAccountChange: onAccountChange,
    placeholder,
    profileName,
    required,
  } = props

  const [showSuggestions, setShowSuggestions] = useState(false)
  const searchRef = useRef(null)
  const suggestionListRef = useRef(null)

  const closeSuggestions = () => setShowSuggestions(false)

  const handleAccountChange = (account) => {
    const isPro = get(account, IS_ACCOUNT_PRO, false)
    const isConnected = get(account, IS_ACCOUNT_CONNECTED, false)
    onAccountChange({
      [PROFILE_NAME]: get(account, PROFILE_NAME, ''),
      [ACCOUNT_ID]: get(account, ACCOUNT_ID, null),
      [IS_ACCOUNT_CONNECTED]: isConnected,
      [IS_ACCOUNT_PRO]: isPro,
    })
  }

  const handleInputChange = (name) => {
    handleAccountChange({ [PROFILE_NAME]: name })
    setShowSuggestions(name.length > 0)
  }

  const handleSuggestionClick = (account) => {
    handleAccountChange(account)
    closeSuggestions()
  }

  const handleOutsideClickRef = useRef(null)
  handleOutsideClickRef.current = ({ target }) => {
    const searchRefContainsTarget =
      searchRef.current && searchRef.current.contains(target)
    const suggestionListRefContainsTarget =
      suggestionListRef.current && suggestionListRef.current.contains(target)
    if (
      !searchRefContainsTarget &&
      !suggestionListRefContainsTarget &&
      showSuggestions
    ) {
      closeSuggestions()
    }
  }

  useEffect(() => {
    const handler = (e) => handleOutsideClickRef.current?.(e)
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [])

  const formatLocationPart = (part1, part2) => {
    const separator = part1 ? ' ' : ''
    return part1 || part2 ? `${part1 || ''}${separator}${part2 || ''}` : null
  }

  const renderTooltipContent = (firstPart, secondPart, thirdPart) => (
    <div>
      {firstPart && (
        <>
          <span>{firstPart}</span>
          <br />
        </>
      )}
      {secondPart && (
        <>
          <span>{secondPart}</span>
          <br />
        </>
      )}
      {thirdPart && (
        <>
          <span>{thirdPart}</span>
        </>
      )}
    </div>
  )

  const renderTooltip = (location) => {
    const { address1, address2, city, state, country, zip } = location
    const [firstPart, secondPart, thirdPart] = [
      [address1, address2],
      [city, state],
      [country, zip],
    ].map((pair) => formatLocationPart(...pair))
    const somePartsNonEmpty = firstPart || secondPart || thirdPart

    return (
      somePartsNonEmpty && (
        <Tooltip
          content={renderTooltipContent(firstPart, secondPart, thirdPart)}
          position="right center"
          trigger={
            <div className={styles.infoIconWrapper}>
              <Icon name="infoBubble" className={styles.infoIcon} />
            </div>
          }
        />
      )
    )
  }

  const suggestionMatchesInput = (suggestion) =>
    sanitizeProfileName(suggestion.toLowerCase()).includes(
      sanitizeProfileName(profileName.toLowerCase()),
    )

  const buildSuggestionForAccount = (account) => {
    const { id, name, isConnected, isPro, location } = account
    const tooltip = location ? renderTooltip(location) : null

    return (
      <div
        key={id}
        onClick={() => handleSuggestionClick(account)}
        className={styles.suggestion}
      >
        <div className={styles.suggestionContent}>
          <div className={styles.suggestionWrapper}>
            {tooltip}
            <div
              className={classNames(styles.suggestionText, {
                [styles.locationIcon]: !!tooltip,
              })}
            >
              <div className={classNames(styles.name, 'notranslate')}>
                {name}
              </div>
              {isConnected && (
                <div className={styles.isConnected}>Is a Connection</div>
              )}
            </div>
          </div>
          {isPro && <ProLiteIndicator isProAccount />}
        </div>
      </div>
    )
  }

  const renderAccountOptions = () =>
    accounts
      .filter(
        ({ name }) => !filterAccountsByInput || suggestionMatchesInput(name),
      )
      .map(buildSuggestionForAccount)

  const labelClass = classNames(styles.nameLabel, {
    [styles.required]: required,
  })
  const inputClass = classNames(styles.input, {
    [styles.suggestionsOpen]: showSuggestions,
  })

  return (
    <div className={styles.AccountDropdown}>
      <label className={labelClass} htmlFor="profileName">
        {`${accountType} Name`}
      </label>
      <div className={styles.inputWrapper} ref={searchRef}>
        <Input
          className={inputClass}
          name="profileName"
          autoComplete="off"
          icon={<Icon className={styles.searchIcon} name="searchSmall" />}
          onChange={({ target: { value } }) => handleInputChange(value)}
          placeholder={placeholder}
          value={profileName}
        />
        {showSuggestions && (
          <div className={styles.suggestionList} ref={suggestionListRef}>
            {renderAccountOptions()}
          </div>
        )}
      </div>
    </div>
  )
}

AccountDropdown.propTypes = {
  accountType: PropTypes.oneOf([BRAND, RETAILER]).isRequired,
  accounts: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      name: PropTypes.string,
      isConnected: PropTypes.bool,
      isPro: PropTypes.bool,
      salesRepEmails: PropTypes.arrayOf(PropTypes.string),
      location: PropTypes.object,
    }),
  ).isRequired,
  filterAccountsByInput: PropTypes.bool,
  handleAccountChange: PropTypes.func.isRequired,
  placeholder: PropTypes.string,
  profileName: PropTypes.string.isRequired,
  required: PropTypes.bool,
}

AccountDropdown.defaultProps = {
  filterAccountsByInput: false,
  placeholder: '',
  required: false,
}

export default AccountDropdown
